import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Stack,
  Divider,
  InputAdornment,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  DirectionsBus as BusIcon,
  Schedule as ScheduleIcon,
  Groups as GroupsIcon,
  Notes as NotesIcon,
  Speed as SpeedIcon,
} from "@mui/icons-material";
import { Formik, Form, Field, useFormikContext } from "formik";
import * as Yup from "yup";
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { bookingValidationSchema } from "./validationSchema";
import { formatDateToDatetimeLocal } from "./utils/formatDate";
import { BookingDetailsProps, BookingFormData } from "./types";
import { partyApi } from "../masterData/party/apiService";
import BusinessIcon from "@mui/icons-material/Business";
import { vehicleExpenseApi } from "../vehicleExpense/apiService";
import { getValidToken } from "../../utils/authUtils";
import { bookingApi } from "./apiService";
import { useToast } from "../../components/Toast";
import { useNavigate, useParams } from "react-router-dom";
import { Loading } from "../../components/Loading";
import { marketLoadApi } from "../masterData/marketLoad/apiService";
import { locations } from "../../constants";
import {
  commonButtonProps,
  commonDateFieldProps,
  commonTextFieldProps,
  smallButtonProp,
} from "../../utils/styles";

const BookingDetails: React.FC<BookingDetailsProps> = ({
  // data,
  // onChange,
  activeStep,
  setActiveStep,
  handleBack,
  handleNext,
  handleSave,
  // isLoading,
  steps,
  setRentalJourneyId,
  setVehicle,
}) => {
  const [selectedVehicle, setSelectedVehicle] = useState<any | null>(null);
  const [partyOptions, setPartyOptions] = useState<any>([]);
  const [marketLoadOptions, setMarketLoadOptions] = useState<any>([]);
  const [searchPartyLoading, setSearchPartyLoading] = useState(false);
  const [searchMarketLoadLoading, setSearchMarketLoadLoading] = useState(false);
  const [vehicleLoading, setVehicleLoading] = useState(false);
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { id } = useParams();
  const [bookingDetails, setBookingDetails] = useState<BookingFormData>({
    partyData: {
      partyName: "",
      primaryContactName: "",
      primaryContactNumber: "",
      email: "",
      address: "",
    },
    partyId: null,
    vehicleId: null,
    vehicleNumber: null,
    odometerReading: 0,
    remarks: null,
    journeyStartDateTime: null,
    journeyEndDateTime: null,
    journeyStartLocation: "",
    journeyEndLocation: "",
    vehicleType: "OWNED",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingDataToDelete, setBookingDataToDelete] = useState<any>(null);
  const [isChanged, setIsChanged] = useState(false);
  const [deletedBookings, setDeletedBookings] = useState<any>([]);
  const [ownedVehicles, setOwnedVehicles] = useState<any>([]);
  const [outsourcedVehicles, setOutsourcedVehicles] = useState<any>([]);
  const [addNotes, setAddNotes] = useState(false);
  const searchParty = useCallback(async (term: string) => {
    setSearchPartyLoading(true);
    if (term?.length < 2 && term?.length > 0) return;

    try {
      const data = await partyApi.search(term);
      setPartyOptions(data);
    } catch (error) {
      console.error("Error searching parties:", error);
    } finally {
      setSearchPartyLoading(false);
    }
  }, []);

  const searchMarketLoad = useCallback(async (term: string) => {
    setSearchMarketLoadLoading(true);
    if (term?.length < 2 && term?.length > 0) return;
    try {
      const data = await marketLoadApi.search(term);
      setMarketLoadOptions(data);
    } catch (error) {
      console.error("Error searching market loads:", error);
    } finally {
      setSearchMarketLoadLoading(false);
    }
  }, []);

  const loadVehicles = useCallback(
    async (vehicleType?: string) => {
      const typeToUse = vehicleType || bookingDetails?.vehicleType;
      if (!typeToUse) return;

      setVehicleLoading(true);
      try {
        if (typeToUse === "OWNED") {
          const vehicleData = await vehicleExpenseApi.fetchVehicles({
            acquisitionType: "Owned",
          });
          setOwnedVehicles(vehicleData);
        } else if (typeToUse === "OUTSOURCED") {
          const outsourcedVehicleData = await vehicleExpenseApi.fetchVehicles({
            acquisitionType: "Outsourced",
          });
          setOutsourcedVehicles(outsourcedVehicleData);
        }
      } catch (error) {
        console.error("Error loading vehicles:", error);
      } finally {
        setVehicleLoading(false);
      }
    },
    [bookingDetails?.vehicleType]
  );

  const loadBookingDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      const bookingData = await bookingApi.getBookingDetails(id!);
      if (bookingData) {
        setBookingDetails(bookingData);
        setVehicle({
          vehicleId: bookingData.vehicle.id,
          vehicleNumber: bookingData.vehicle.vehicleNumber,
          vehicleType: bookingData.vehicle.acquisitionType,
        });
        setBookingDataToDelete(bookingData);
        setIsChanged(false);
        // Set the selected vehicle
        if (bookingData.vehicle) {
          setSelectedVehicle(bookingData.vehicle);
        }
        const deletedBookings = await bookingApi.getDeletedBookings(id!);
        if (deletedBookings) {
          setDeletedBookings(deletedBookings);
        }
      }
    } catch (error) {
      console.error("Error loading booking details:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVehicles();
  }, [bookingDetails?.vehicleType]);

  useEffect(() => {
    searchParty("");
    searchMarketLoad("");
    if (id !== "new") {
      loadBookingDetails();
    }
  }, []);

  const handleSubmit = async (values: BookingFormData) => {
    setIsSubmitting(true);
    if (id === "new") {
      // Create a new object without the id field
      const { id: _, ...createData } = values;
      const response = await bookingApi.createBooking(createData);
      if (response) {
        // setActiveStep((prevStep: any) => prevStep + 1);
        // setRentalJourneyId(response?.id || "");
        if (values?.vehicleId === null) {
          navigate(`/rental/${response?.id}/logs?type=SCHEDULED&tab=INCOME`);
        } else {
          navigate(
            `/rental/${response?.id}/logs?type=${values?.vehicleType}&tab=LOGS&vehicleId=${values?.vehicleId}&vehicleNumber=${values?.vehicleNumber}`
          );
        }
      } else {
        showToast("Failed to create booking", "error");
      }
    } else {
      try {
        if (isChanged) {
          // First mark the old booking as deleted
          // const deleteData = {
          //   ...bookingDataToDelete,
          //   deletedAt: new Date().toISOString(),
          // };
          // const { id: _, ...createData } = deleteData;
          // await bookingApi.createBooking(createData);

          // Then create a new booking with the updated values
          const response = await bookingApi.updateBooking(id!, values);
          if (response) {
            setActiveStep((prevStep: any) => prevStep + 1);
            setRentalJourneyId(response?.id || "");
          } else {
            showToast("Failed to create new booking", "error");
          }
        } else {
          // No changes, just proceed to next step
          setActiveStep((prevStep: any) => prevStep + 1);
        }
      } catch (error) {
        console.error("Error updating booking:", error);
        showToast("Failed to update booking", "error");
      }
    }
    setIsSubmitting(false);
  };

  const validateStep = (values: BookingFormData) => {
    return true;
  };

  if (isLoading || isSubmitting) {
    return isSubmitting ? (
      <Loading message="Submitting..." fullScreen={true} />
    ) : (
      <Loading fullScreen={true} />
    );
  }

  const handleMarketLoadChange = (
    setFieldValue: any,
    marketLoad: any,
    inputValue?: any
  ) => {
    if (marketLoad) {
      setFieldValue("marketLoadId", marketLoad?.id || "");
      setVehicle({
        vehicleId: null,
        vehicleNumber: marketLoad?.vehicleNumber || "",
      });
      setFieldValue("vehicleId", null);
      setFieldValue("vehicleNumber", marketLoad?.vehicleNumber || "");
      setFieldValue(
        "marketLoadData.vehicleNumber",
        marketLoad?.vehicleNumber || null
      );
      setFieldValue(
        "marketLoadData.vehicleCompanyName",
        marketLoad?.vehicleCompanyName || null
      );
      setFieldValue(
        "marketLoadData.companyName",
        marketLoad?.companyName || null
      );
      setFieldValue("marketLoadData.gstNumber", marketLoad?.gstNumber || null);
      setFieldValue("marketLoadData.ownerName", marketLoad?.ownerName || null);
      setFieldValue(
        "marketLoadData.ownerContactNumber",
        marketLoad?.ownerContactNumber || null
      );
      setFieldValue(
        "marketLoadData.primaryContactName",
        marketLoad?.primaryContactName || null
      );
      setFieldValue(
        "marketLoadData.primaryContactNumber",
        marketLoad?.primaryContactNumber || null
      );
    } else if (inputValue && inputValue.trim() !== "") {
      setFieldValue("marketLoadId", null);
      setFieldValue("vehicleId", null);
      setFieldValue("vehicleNumber", inputValue.trim());
      setFieldValue("marketLoadData.vehicleNumber", inputValue.trim());
    } else {
      setFieldValue("marketLoadId", null);
      setFieldValue("marketLoadData.vehicleNumber", null);
      setFieldValue("marketLoadData.vehicleId", null);
      setFieldValue("marketLoadData.vehicleCompanyName", null);
      setFieldValue("marketLoadData.companyName", null);
      setFieldValue("marketLoadData.gstNumber", null);
      setFieldValue("marketLoadData.ownerName", null);
      setFieldValue("marketLoadData.ownerContactNumber", null);
      setFieldValue("marketLoadData.primaryContactName", null);
      setFieldValue("marketLoadData.primaryContactNumber", null);
    }
  };

  const handlePartyChange = (
    setFieldValue: any,
    party: any,
    inputValue?: any
  ) => {
    if (party) {
      setFieldValue("partyData.partyName", party?.partyName || "");
      setFieldValue("partyId", party?.id || "");
      setFieldValue(
        "partyData.primaryContactName",
        party?.primaryContactName || ""
      );
      setFieldValue(
        "partyData.primaryContactNumber",
        party?.primaryContactNumber || ""
      );
      setFieldValue("partyData.email", party?.email || "");
      setFieldValue("partyData.address", party?.address || "");
    } else if (inputValue && inputValue.trim() !== "") {
      setFieldValue("partyId", null);
      setFieldValue("partyData.partyName", inputValue.trim());
      setFieldValue("partyData.primaryContactName", "");
      setFieldValue("partyData.primaryContactNumber", "");
      setFieldValue("partyData.email", "");
      setFieldValue("partyData.address", "");
    } else {
      setFieldValue("partyData.partyName", "");
      setFieldValue("partyId", null);
      setFieldValue("partyData.primaryContactName", "");
      setFieldValue("partyData.primaryContactNumber", "");
      setFieldValue("partyData.email", "");
      setFieldValue("partyData.address", "");
    }
  };

  return (
    <Formik
      initialValues={bookingDetails}
      validationSchema={bookingValidationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        setFieldValue,
        isValid,
        resetForm,
      }) => {
        return (
          <Form>
            <Paper
              elevation={3}
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                p: 2,
              }}
            >
              {/* Party Information Section */}
              <Box sx={{ mb: 2 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <PersonIcon color="primary" sx={{ fontSize: "1rem" }} />
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      color: "text.primary",
                    }}
                  >
                    Party Information
                  </Typography>
                </Stack>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <Autocomplete
                      options={partyOptions}
                      value={
                        values?.partyData?.partyName ? values.partyData : null
                      }
                      getOptionLabel={(option: any) => option?.partyName || ""}
                      freeSolo
                      onInputChange={(_, newValue, reason) => {
                        searchParty(newValue);
                        if (reason === "input") {
                          const matchingOption = partyOptions.find(
                            (option: any) =>
                              option?.partyName
                                .toLowerCase()
                                .includes(newValue.toLowerCase())
                          );
                          if (!matchingOption && newValue.trim() !== "") {
                            handlePartyChange(setFieldValue, null, newValue);
                          }
                        }
                      }}
                      onChange={(_, party: any, reason: any) => {
                        if (
                          reason === "selectOption" &&
                          typeof party === "object"
                        ) {
                          setFieldValue(
                            "partyData.partyName",
                            party?.partyName || ""
                          );
                          setFieldValue("partyId", party?.id || "");
                          setFieldValue(
                            "partyData.primaryContactName",
                            party?.primaryContactName || ""
                          );
                          setFieldValue(
                            "partyData.primaryContactNumber",
                            party?.primaryContactNumber || ""
                          );
                          setFieldValue("partyData.email", party?.email || "");
                          setFieldValue(
                            "partyData.address",
                            party?.address || ""
                          );
                        } else if (reason === "clear") {
                          setFieldValue("partyData.partyName", "");
                          setFieldValue("partyId", null);
                          setFieldValue("partyData.primaryContactName", "");
                          setFieldValue("partyData.primaryContactNumber", "");
                          setFieldValue("partyData.email", "");
                          setFieldValue("partyData.address", "");
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Party Name"
                          placeholder="Search for existing party or type new name"
                          required
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {searchPartyLoading ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                          {...commonTextFieldProps}
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 500, fontSize: "0.75rem" }}
                          >
                            {option?.partyName} ({option?.primaryContactName})
                          </Typography>
                        </Box>
                      )}
                    />
                    {touched.partyData?.partyName &&
                      errors.partyData?.partyName && (
                        <Typography color="error" variant="caption">
                          {errors.partyData?.partyName}
                        </Typography>
                      )}
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <Field
                      as={TextField}
                      fullWidth
                      label="Primary Contact Name"
                      name="partyData.primaryContactName"
                      value={values?.partyData?.primaryContactName}
                      onChange={(e: any) => {
                        handleChange(e);
                      }}
                      placeholder="Enter contact person name"
                      required
                      error={
                        touched.partyData?.primaryContactName &&
                        Boolean(errors.partyData?.primaryContactName)
                      }
                      helperText={
                        touched.partyData?.primaryContactName &&
                        errors.partyData?.primaryContactName
                      }
                      {...commonTextFieldProps}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <Field
                      as={TextField}
                      fullWidth
                      label="Phone Number"
                      name="partyData.primaryContactNumber"
                      value={values?.partyData?.primaryContactNumber}
                      onChange={(e: any) => {
                        handleChange(e);
                      }}
                      placeholder="Enter phone number"
                      required
                      error={
                        touched.partyData?.primaryContactNumber &&
                        Boolean(errors.partyData?.primaryContactNumber)
                      }
                      helperText={
                        touched.partyData?.primaryContactNumber &&
                        errors.partyData?.primaryContactNumber
                      }
                      inputProps={{
                        maxLength: 10,
                      }}
                      {...commonTextFieldProps}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <Field
                      as={TextField}
                      fullWidth
                      label="Email Address"
                      type="email"
                      name="partyData.email"
                      value={values?.partyData?.email}
                      onChange={(e: any) => {
                        handleChange(e);
                      }}
                      placeholder="Enter email address (optional)"
                      error={
                        touched.partyData?.email &&
                        Boolean(errors.partyData?.email)
                      }
                      helperText={
                        touched.partyData?.email && errors.partyData?.email
                      }
                      {...commonTextFieldProps}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Field
                      as={TextField}
                      fullWidth
                      label="Address"
                      multiline
                      rows={1}
                      name="partyData.address"
                      value={values?.partyData?.address}
                      onChange={(e: any) => {
                        handleChange(e);
                      }}
                      placeholder="Enter full address (optional)"
                      error={
                        touched.partyData?.address &&
                        Boolean(errors.partyData?.address)
                      }
                      helperText={
                        touched.partyData?.address && errors.partyData?.address
                      }
                      {...commonTextFieldProps}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Trip Information Section */}
              <Box sx={{ mb: 2 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <ScheduleIcon color="primary" sx={{ fontSize: "1rem" }} />
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      color: "text.primary",
                    }}
                  >
                    Trip Information
                  </Typography>
                </Stack>

                <Grid container spacing={2}>
                  {/* Route Information */}
                  <Grid item xs={12} md={2}>
                    <Autocomplete
                      freeSolo
                      options={locations}
                      value={values?.journeyStartLocation}
                      onChange={(_, newValue) => {
                        setFieldValue("journeyStartLocation", newValue || "");
                        setIsChanged(true);
                      }}
                      onInputChange={(_, newInputValue) => {
                        setFieldValue("journeyStartLocation", newInputValue);
                        setIsChanged(true);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="From Location"
                          placeholder="Enter departure location"
                          required
                          error={
                            touched.journeyStartLocation &&
                            Boolean(errors.journeyStartLocation)
                          }
                          helperText={
                            touched.journeyStartLocation &&
                            errors.journeyStartLocation
                          }
                          InputProps={{
                            ...params.InputProps,
                          }}
                          {...commonTextFieldProps}
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 500, fontSize: "0.75rem" }}
                          >
                            {option}
                          </Typography>
                        </Box>
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <Autocomplete
                      freeSolo
                      options={locations}
                      value={values?.journeyEndLocation}
                      onChange={(_, newValue) => {
                        setFieldValue("journeyEndLocation", newValue || "");
                        setIsChanged(true);
                      }}
                      onInputChange={(_, newInputValue) => {
                        setFieldValue("journeyEndLocation", newInputValue);
                        setIsChanged(true);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="To Location"
                          placeholder="Enter destination location"
                          required
                          error={
                            touched.journeyEndLocation &&
                            Boolean(errors.journeyEndLocation)
                          }
                          helperText={
                            touched.journeyEndLocation &&
                            errors.journeyEndLocation
                          }
                          InputProps={{
                            ...params.InputProps,
                          }}
                          {...commonTextFieldProps}
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 500, fontSize: "0.75rem" }}
                          >
                            {option}
                          </Typography>
                        </Box>
                      )}
                    />
                  </Grid>

                  {/* Journey Dates */}
                  <Grid item xs={12} md={2.5}>
                    <Field
                      as={TextField}
                      fullWidth
                      type="datetime-local"
                      label="Start Date & Time"
                      name="journeyStartDateTime"
                      value={formatDateToDatetimeLocal(
                        values?.journeyStartDateTime
                      )}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFieldValue("journeyStartDateTime", e.target.value);
                        setIsChanged(true);
                      }}
                      required
                      error={
                        touched.journeyStartDateTime &&
                        Boolean(errors.journeyStartDateTime)
                      }
                      helperText={
                        touched.journeyStartDateTime &&
                        errors.journeyStartDateTime
                      }
                      {...commonDateFieldProps}
                    />
                  </Grid>

                  <Grid item xs={12} md={2.5}>
                    <Field
                      as={TextField}
                      fullWidth
                      type="datetime-local"
                      label="Return Date & Time"
                      name="journeyEndDateTime"
                      value={formatDateToDatetimeLocal(
                        values?.journeyEndDateTime
                      )}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFieldValue("journeyEndDateTime", e.target.value);
                        setIsChanged(true);
                      }}
                      error={
                        touched.journeyEndDateTime &&
                        Boolean(errors.journeyEndDateTime)
                      }
                      helperText={
                        touched.journeyEndDateTime && errors.journeyEndDateTime
                      }
                      {...commonDateFieldProps}
                    />
                  </Grid>

                  {/* Additional Details */}

                  <Grid item xs={6} md={2} sx={{ display: "flex", alignItems: "center" }}>
                    <Button
                      variant="outlined"
                      onClick={() => setAddNotes(!addNotes)}
                      size={smallButtonProp.size}
                      sx={{
                        ...smallButtonProp.sx,
                        color: "grey",
                        borderColor: "grey.400",
                        "&:hover": {
                          borderColor: "grey.400",
                          color: "grey",
                        },
                      }}
                    >
                      <Typography variant="body2" sx={{ fontSize: "0.65rem" }}>
                        {addNotes ? "Hide Remarks" : "Add Remarks"}
                      </Typography>
                    </Button>
                  </Grid>
                  {addNotes && (
                    <Grid item xs={12}>
                      <Field
                        as={TextField}
                        fullWidth
                        label="Remarks"
                        multiline
                        rows={1}
                        name="remarks"
                        value={values?.remarks}
                        onChange={(e: any) => {
                          handleChange(e);
                          setIsChanged(true);
                        }}
                        placeholder="Enter any remarks or notes for this rental booking"
                        error={touched.remarks && Boolean(errors.remarks)}
                        helperText={touched.remarks && errors.remarks}
                        {...commonTextFieldProps}
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Vehicle Information Section */}
              <Box sx={{ mb: 2 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <BusIcon color="primary" sx={{ fontSize: "1rem" }} />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      color: "text.primary",
                    }}
                  >
                    Assign Vehicle (Optional)
                  </Typography>
                  <ToggleButtonGroup
                    value={values?.vehicleType}
                    exclusive
                    disabled={vehicleLoading}
                    onChange={async (e, value) => {
                      if (value !== null) {
                        setFieldValue("vehicleType", value);
                        // Clear previous vehicle selection
                        setFieldValue("vehicleId", null);
                        setFieldValue("vehicleNumber", "");
                        setSelectedVehicle(null);
                        setVehicle({
                          vehicleId: null,
                          vehicleNumber: "",
                          vehicleType: value,
                        });

                        // Fetch vehicles based on the new vehicle type
                        // Only fetch if we don't already have vehicles for this type
                        if (
                          (value === "OWNED" && ownedVehicles.length === 0) ||
                          (value === "OUTSOURCED" &&
                            outsourcedVehicles.length === 0)
                        ) {
                          await loadVehicles(value);
                        }
                      }
                    }}
                    size="small"
                    sx={{ height: "28px" }}
                  >
                    <ToggleButton
                      value="OWNED"
                      sx={{
                        "&.Mui-selected": {
                          backgroundColor: "primary.main",
                          color: "#fff",
                          "&:hover": {
                            backgroundColor: "primary.main",
                          },
                        },
                        fontSize: "0.6rem",
                        padding: "0px 2px",
                        textTransform: "none",
                      }}
                    >
                      Owned
                    </ToggleButton>
                    <ToggleButton
                      value="OUTSOURCED"
                      sx={{
                        "&.Mui-selected": {
                          backgroundColor: "primary.main",
                          color: "#fff",
                          "&:hover": {
                            backgroundColor: "primary.main",
                          },
                        },
                        fontSize: "0.6rem",
                        padding: "0px 2px",
                        textTransform: "none",
                      }}
                    >
                      Outsourced
                    </ToggleButton>
                  </ToggleButtonGroup>
                  {vehicleLoading && (
                    <CircularProgress size={16} sx={{ ml: 1 }} />
                  )}
                </Stack>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <Autocomplete
                      disabled={!values?.vehicleType}
                      options={
                        values?.vehicleType === "OWNED"
                          ? ownedVehicles
                          : outsourcedVehicles
                      }
                      noOptionsText={
                        vehicleLoading
                          ? "Loading vehicles..."
                          : values?.vehicleType
                          ? "No vehicles available for this type"
                          : "Please select vehicle type first"
                      }
                      getOptionLabel={(option) => `${option?.vehicleNumber}`}
                      value={selectedVehicle}
                      onChange={(_, newValue) => {
                        const vehicleType =
                          newValue?.acquisitionType === "Outsourced"
                            ? "OUTSOURCED"
                            : "OWNED";
                        setSelectedVehicle(newValue);
                        setVehicle({
                          vehicleId: newValue?.id || null,
                          vehicleNumber: newValue?.vehicleNumber || "",
                          vehicleType: vehicleType || "",
                        });
                        setFieldValue("vehicleId", newValue?.id || null);
                        setFieldValue(
                          "vehicleNumber",
                          newValue?.vehicleNumber || ""
                        );
                        setFieldValue("vehicleType", vehicleType || "");
                        setIsChanged(true);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Vehicle"
                          placeholder={
                            values?.vehicleType
                              ? "Choose a vehicle"
                              : "Please select vehicle type first"
                          }
                          error={
                            (touched.vehicleId && Boolean(errors.vehicleId)) ||
                            (touched.vehicleNumber &&
                              Boolean(errors.vehicleNumber))
                          }
                          helperText={
                            (touched.vehicleId && errors.vehicleId) ||
                            (touched.vehicleNumber && errors.vehicleNumber)
                          }
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {vehicleLoading && (
                                  <CircularProgress size={20} />
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                          {...commonTextFieldProps}
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Stack spacing={0.5} sx={{ width: "100%" }}>
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 500, fontSize: "0.75rem" }}
                            >
                              {option?.vehicleNumber}
                            </Typography>
                          </Stack>
                        </Box>
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Summary Section */}
              {selectedVehicle &&
                values?.journeyStartLocation &&
                values?.journeyEndLocation && (
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      backgroundColor: "primary.lighter",
                      border: "1px solid",
                      borderColor: "primary.light",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 1,
                        fontWeight: 600,
                        fontSize: "1rem",
                      }}
                    >
                      Booking Summary
                    </Typography>

                    <Grid container spacing={1}>
                      <Grid item xs={12} md={4}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <PersonIcon
                            sx={{ fontSize: "1rem" }}
                            color="primary"
                          />
                          <Typography
                            variant="body1"
                            sx={{ fontSize: "0.75rem" }}
                          >
                            <strong>{values?.partyData?.partyName}</strong>
                            {values?.partyData?.primaryContactName &&
                              ` (Contact: ${values?.partyData?.primaryContactName})`}
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <BusIcon sx={{ fontSize: "1rem" }} color="primary" />
                          <Typography
                            variant="body1"
                            sx={{ fontSize: "0.75rem" }}
                          >
                            {selectedVehicle?.vehicleNumber} -{" "}
                            {selectedVehicle?.acquisitionType} (
                            {selectedVehicle?.seatingCapacity} seats)
                          </Typography>
                        </Stack>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <LocationIcon
                            sx={{ fontSize: "1rem" }}
                            color="primary"
                          />
                          <Typography
                            variant="body1"
                            sx={{ fontSize: "0.75rem" }}
                          >
                            {values?.journeyStartLocation} →{" "}
                            {values?.journeyEndLocation}
                          </Typography>
                        </Stack>
                      </Grid>

                      {values?.journeyStartDateTime && (
                        <Grid item xs={12} md={4}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <ScheduleIcon
                              sx={{ fontSize: "1rem" }}
                              color="primary"
                            />
                            <Typography
                              variant="body1"
                              sx={{ fontSize: "0.75rem" }}
                            >
                              {new Date(
                                values?.journeyStartDateTime
                              ).toLocaleString()}
                              {values?.journeyEndDateTime &&
                                ` → Return: ${new Date(
                                  values?.journeyEndDateTime
                                ).toLocaleString()}`}
                            </Typography>
                          </Stack>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                )}
            </Paper>

            <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                type="submit"
                disabled={isSubmitting}
                {...commonButtonProps}
              >
                Save & Continue
              </Button>
            </Box>

            {/* Navigation Buttons */}
            {/* <Paper
              elevation={1}
              sx={{
                p: 1,
                mt: 2,
                borderRadius: 2,
                backgroundColor: "grey.50",
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
              >
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBack}
                  disabled={activeStep === 0}
                  sx={{ minWidth: 120, height: 30, fontSize: "0.7rem" }}
                >
                  Previous
                </Button>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: { xs: "none", sm: "block" },
                    fontSize: "0.8rem",
                  }}
                >
                  Step {activeStep + 1} of{" "}
                  {id === "schedule" ? steps.length - 1 : steps.length}
                </Typography>

                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  type="submit"
                  disabled={!isValid}
                  sx={{ minWidth: 120, height: 30, fontSize: "0.7rem" }}
                >
                  Next
                </Button>
              </Stack>
            </Paper> */}
          </Form>
        );
      }}
    </Formik>
  );
};

export default BookingDetails;
